import { ReactElement } from "react";
import { useDependency } from "../../../../../contexts/DependencyContext";
import { useTimeMachine } from "../../../../../contexts/TimeMachineContext";
import { useAsyncCall } from "../../../../../hooks/UseAsyncCall";
import { DataSourceModelService, IDataSourceModel } from "../../../../../services/DataSourceModelService";
import { IComponent } from "../../../../../services/http/component/models/IComponent";
import { INumericalSensorConfiguration } from "../../../../../services/http/component/models/INumericalSensorConfiguration";
import { DataSource } from "../../../../../services/http/datasource/models/DataSource";
import { MeasureSummary } from "../../../../../services/http/measure/models/MeasureSummary";
import Spinner from "../../../../../components/Shared/Spinner";
import SensorInfoItem from "./SensorInfoItem";
import styles from "./SensorInfo.module.scss";

interface SensorInfoProps
{
    component: IComponent;
}

async function loadData(service: DataSourceModelService, dataSourceId: number, timeMachineDate: Date): Promise<IDataSourceModel>
{
    return await service.getDataSourceData(dataSourceId, timeMachineDate);
}

function SensorInfo(props: SensorInfoProps): ReactElement
{
    const service = useDependency(DataSourceModelService);
    const timeMachine = useTimeMachine();
    const configuration = JSON.parse(props.component.uiConfiguration) as INumericalSensorConfiguration;
    const { loading, content } = useAsyncCall(loadData, service, configuration.dataSourceId, timeMachine);
    const dataSource = content?.dataSource || new DataSource();
    const measureSummary = content?.measureSummary || new MeasureSummary();

    return loading
        ? <Spinner caption="Loading data..." />
        : (
            <div className={styles.sensorInfo}>
                <div className={styles.highlightedItem}>
                    <div className={styles.dataValue}>
                        {measureSummary.currentValue?.toFixed(2)}
                        {dataSource.unitOfMeasureCode}
                    </div>
                    <div className={styles.dataLabel}>Last Value</div>
                </div>

                <h4>Historical</h4>

                <SensorInfoItem
                    label="Average"
                    value={measureSummary.historicMeasure?.average.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Standard deviation"
                    value={measureSummary.historicMeasure?.standardDeviation.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Maximum"
                    value={measureSummary.historicMeasure?.maximum.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Minimum"
                    value={measureSummary.historicMeasure?.minimum.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Median"
                    value={measureSummary.historicMeasure?.median.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <h4>Last month</h4>

                <SensorInfoItem
                    label="Average"
                    value={measureSummary.lastMonthMeasure?.average.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Standard deviation"
                    value={measureSummary.lastMonthMeasure?.standardDeviation.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Maximum"
                    value={measureSummary.lastMonthMeasure?.maximum.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Minimum"
                    value={measureSummary.lastMonthMeasure?.minimum.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />

                <SensorInfoItem
                    label="Median"
                    value={measureSummary.lastMonthMeasure?.median.toFixed(2)}
                    uom={dataSource.unitOfMeasureCode} />
            </div>
        );
}

export default SensorInfo;
